import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  input,
  inject
} from '@angular/core';
import { ResponsiveProp } from '../skeleton-grid/skeleton-grid.types';
import { SkeletonBreakpointService } from '../skeleton-grid/skeleton-breakpoint.service';
import { resolveResponsiveProp } from '../skeleton-grid/skeleton-grid.utils';

@Component({
  selector: 'umss-skeleton',
  standalone: true,
  imports: [],
  templateUrl: './skeleton.component.html',
  styleUrls: ['./skeleton.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--skeleton-item-col]': 'computedCol()',
    '[style.--skeleton-item-row]': 'computedRow()'
  }
})
export class UmssSkeletonComponent {
  private responsiveService = inject(SkeletonBreakpointService);

  @HostBinding('style.display') display = 'contents';

  /** Shape of the skeleton */
  variant = input<'Avatar' | 'Line' | 'Object' | 'Button'>('Object');

  /** Optional custom width */
  spacingX = input<number>();

  /** Optional custom height */
  spacingY = input<number>();

  /** Optional custom class */
  className = input<string>();

  styles = input<Record<string, string>>({});

  customStyles = computed<Record<string, string>>(() => {
    const defaultStyles: Record<string, string> = {};
    const spaceX = this.spacingX();
    const spaceY = this.spacingY();

    if (spaceX !== undefined) {
      defaultStyles['maxWidth'] = `${spaceX * 4}px`;
    }
    if (spaceY !== undefined) {
      defaultStyles['maxHeight'] = `${spaceY * 4}px`;
    }

    return {
      ...defaultStyles,
      ...this.styles()
    };
  });

  // --- Grid Item Layout API ---
  colSpan = input<ResponsiveProp>();
  colStart = input<ResponsiveProp>();
  rowSpan = input<ResponsiveProp>();
  rowStart = input<ResponsiveProp>();

  computedCol = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    const start = resolveResponsiveProp(this.colStart(), bp, '');
    const span = resolveResponsiveProp(this.colSpan(), bp, '');

    if (!start && !span) return 'auto';
    if (typeof span === 'string' && span.includes('/')) return span;

    const startVal = start ? start : 'auto';
    const spanVal = span
      ? typeof span === 'number' || !isNaN(Number(span))
        ? `span ${span}`
        : span
      : 'auto';
    return `${startVal} / ${spanVal}`;
  });

  classes = computed(() => {
    return `${this.className() || ''} umss-skeleton-base skeleton-${this.variant().toLowerCase()}`;
  });

  computedRow = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    const start = resolveResponsiveProp(this.rowStart(), bp, '');
    const span = resolveResponsiveProp(this.rowSpan(), bp, '');

    if (!start && !span) return 'auto';
    if (typeof span === 'string' && span.includes('/')) return span;

    const startVal = start ? start : 'auto';
    const spanVal = span
      ? typeof span === 'number' || !isNaN(Number(span))
        ? `span ${span}`
        : span
      : 'auto';
    return `${startVal} / ${spanVal}`;
  });
}
